import React from "react";
import locale from "@bonliva-crm/locale";
import { useLocalStore, useObserver } from "mobx-react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  Collapse
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Delete } from "mdi-material-ui";
import { ThemeType } from "@theme";
import { Checkbox, Text } from "@components/formx";
import { DoctorStore, Specialization, AreaOfExpertise } from "./DoctorStore";
import { ExpMultiSelect, ExpTopPanel, ExpKnownSystem, OutlinedInput } from "../Common";
import { ConsultantEditorStore } from "../../ConsultantEditorStore";
import { useConsultantEditorContext } from "../../ConsultantEditorContext";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

type Props = {
  profession: string;
  store: DoctorStore;
};

export function DoctorExpertise(props: Props) {
  const { store } = props;

  const editorStore: ConsultantEditorStore = useConsultantEditorContext();
  const doctorStore: DoctorStore = useLocalStore(() => store);
  const profItem = `professions.${doctorStore.storeIndex}`;
  const classes = useStyles({});

  return useObserver(() => (
    <>
      <Paper className={classes.doctorInfo}>
        <ExpTopPanel
          certifiedPath={`${profItem}.certifiedFrom`}
          certifiedFromTxt="Leg. Läkare:"
          onDeleteClick={doctorStore.deleteExpertise}
          withPrescriptionCode
        />

        <ExpMultiSelect
          name={locale.AddVUBDoc}
          selectOptions={doctorStore.selectAoEList}
          onChange={(v) => doctorStore.setSelectedAoE(v)}
          selectedVal={doctorStore.selectedAoE}
          btnClick={() => doctorStore.addAoE()}
          btnDisabled={!doctorStore.selectedAoE}
        />
      </Paper>

      <DoctorAoE
        areasOfExpertise={doctorStore.areasOfExpertise}
        profItem={profItem}
        profession="Legitimerad läkare"
        onDelete={(i) => doctorStore.removeAOEByIndex(i)}
        onToggle={(i) => doctorStore.toggleAOEByIndex(i)}
      />

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <ExpKnownSystem
              options={doctorStore.systems}
              onChange={doctorStore.addSystems}
              value={doctorStore.selectedSystems}
              hasValidated={true}
              validate={(e, c) => {
                if (c) {
                  editorStore.consultantCVManager.approveKey(
                    `professions.${doctorStore.storeIndex}.systems`
                  );
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  ));
}

export type DoctorAoEProp = {
  areasOfExpertise: AreaOfExpertise[];
  profItem: string;
  profession: string;
  onDelete: (index: number) => void;
  onToggle: (index: number) => void;
};

export function DoctorAoE(props: DoctorAoEProp) {
  const classes = useStyles({});
  const { areasOfExpertise, profItem, profession } = props;

  return (
    <>
      {areasOfExpertise?.map((item, i) => {
        const itemName = `${profItem}.areasOfExpertise.${i}`;
        const defaultAOE = item.name === profession;

        return (
          <Paper key={itemName} className={classes.paperTable}>
            <Table style={{ background: "aliceblue" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    style={{ width: "15%", fontSize: "1.1rem", fontWeight: 500 }}
                  >
                    {item.name}
                  </TableCell>

                  <TableCell style={{ width: 64 }}>
                    <Checkbox
                      name={`${itemName}.qualified`}
                      label={locale.Experienced}
                      FormControlLabelProps={{ style: { whiteSpace: "nowrap" } }}
                    />
                  </TableCell>

                  <TableCell style={{ width: 64 }}>
                    <Checkbox
                      name={`${itemName}.interested`}
                      label={locale.Interested}
                      FormControlLabelProps={{ style: { whiteSpace: "nowrap" } }}
                    />
                  </TableCell>

                  <TableCell style={{ textAlign: "center" }}>
                    <OutlinedInput
                      name={`${itemName}.yearFrom`}
                      label={locale.CertifiedFrom}
                      disabled={defaultAOE}
                    />
                  </TableCell>
                  {/*<TableCell style={{ width: 64 }}>*/}
                  {/*  <Checkbox name={`${itemName}.qualified`} label={locale.Experienced} />*/}
                  {/*</TableCell>*/}

                  <TableCell>
                    <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                  </TableCell>

                  <TableCell style={{ width: 64 }}>
                    <IconButton
                      style={{ padding: 4 }}
                      onClick={() => props.onDelete(i)}
                      disabled={defaultAOE}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>

                  {item.specializations.length !== 0 && (
                    <TableCell style={{ width: 64 }}>
                      <IconButton
                        style={{ padding: 4 }}
                        onClick={() => props.onToggle(i)}
                      >
                        {item.open ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>

            {/*<Divider />*/}

            {item.specializations.length !== 0 && (
              <Collapse in={item.open} timeout="auto" unmountOnExit>
                <DoctorSpecialization
                  specializations={item.specializations}
                  profAoEItem={itemName}
                  qualified={item.qualified}
                  interested={item.interested}
                />
              </Collapse>
            )}
          </Paper>
        );
      })}
    </>
  );
}

type DoctorSpecializationProp = {
  profAoEItem: string;
  specializations: Specialization[];
  qualified: boolean;
  interested: boolean;
};

function DoctorSpecialization(props: DoctorSpecializationProp) {
  const { specializations, profAoEItem, qualified, interested } = props;

  return useObserver(() => (
    <Grid item xs={12}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: "15%" }}>
              {locale.Specialization}
            </TableCell>
            <TableCell align="center" style={{ width: 64 }}>
              {locale.Experienced}
            </TableCell>
            <TableCell align="center" style={{ width: 64, whiteSpace: "nowrap" }}>
              {locale.Interested}
            </TableCell>
            <TableCell align="center">{locale.Comment}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {specializations.map((row, i) => {
            const itemName = `${profAoEItem}.specializations.${i}`;
            if (!qualified) {
              row.qualified = false;
            }

            if (!interested) {
              row.interested = false;
            }
            return (
              <TableRow key={itemName}>
                <TableCell style={{ fontWeight: "bold" }}>{row.name}</TableCell>
                <TableCell align="center">
                  <Checkbox name={`${itemName}.qualified`} disabled={!qualified} />
                </TableCell>
                <TableCell align="center">
                  <Checkbox name={`${itemName}.interested`} disabled={!interested} />
                </TableCell>
                <TableCell align="center">
                  <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Grid>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  doctorInfo: {
    ...theme.mixins.childrenMarginBottom(8),
    position: "relative",
    padding: 16
  },

  paperTable: {
    // margin: theme.spacing(2)
  },

  aoeName: {
    // paddingLeft: theme.spacing(2),
    fontWeight: "bold"
    // paddingTop: theme.spacing(2)
  }
}));
